import React from "react"

import BarChart from "../../Charts/BarStackSection/BarChart"
import ParentSize from "@visx/responsive/lib/components/ParentSize"
import FiltersCharts from "../FiltersCharts"
import { useI18n } from "providers/I18n"
import BaseSwitch from "../../SharedComponents/BaseSwitch"
import { ExpandedLayoutTitleWithActions } from "../DetailsLayout/ExpandLineChartDialogLayout/ExpandedLayoutTitleWithActions"
import { ChartColumn } from "../DetailsLayout/ExpandLineChartDialogLayout/ChartColumn"
import { ChartRootLayoutExpanded } from "../DetailsLayout/ExpandLineChartDialogLayout/ChartRootLayoutExpanded"
import { CloseDialogButton } from "../DetailsLayout/ExpandLineChartDialogLayout/CloseDialogButton"
import { BaseChartDialog } from "../DetailsLayout/ExpandLineChartDialogLayout/BaseChartDialog"
import { FirstBarInfos, SecondBarInfos } from "../NonGCFView"
import { NonGCFData } from "../../../types"
import { Box } from "@material-ui/core"

export type ExpandBarStackDialogRef = {
  open: (item: any, idx: number) => void
}

export type ExpandBarStackDialogProps = {
  data: NonGCFData
  firstBarInfos: FirstBarInfos
  secondBarInfos?: SecondBarInfos
  dataType: "gcf" | "non-gcf"
  search: string
  availableEntities: string[][]
  showSecondBar?: boolean[]
  windowDimensions: { width: number; height: number }
  legendItems: string[][]
  handleChangeAvailableEntities: (idx: number, entity: string) => void
  handleSwitchChange?: (i: number) => void
  handleCheckAllEntities: (idx: number, items: string[]) => void
  selectAllChecked: boolean[]
}

export const ExpandBarStackDialog = React.forwardRef(function ExpandDialog(
  {
    data,
    search,
    availableEntities,
    showSecondBar,
    firstBarInfos,
    secondBarInfos,
    dataType,
    windowDimensions,
    legendItems,
    handleChangeAvailableEntities,
    handleSwitchChange,
    handleCheckAllEntities,
    selectAllChecked,
  }: ExpandBarStackDialogProps,

  ref,
) {
  const [open, setOpen] = React.useState(false)
  const [item, setItem] = React.useState({ key: "", index: 0 })

  const { t } = useI18n()

  const handleClose = React.useCallback(() => {
    setOpen(false)
  }, [])

  React.useImperativeHandle(
    ref,
    () => ({
      open: (key: string, index: number) => {
        setOpen(true)
        setItem({ key, index })
      },
    }),
    [],
  )

  return (
    <BaseChartDialog
      onClose={handleClose}
      open={open}
      width={windowDimensions.width}
    >
      <Box display="flex">
        <ChartColumn>
          <ExpandedLayoutTitleWithActions
            title={
              dataType === "non-gcf"
                ? "USD ($) / OIBIA (%)"
                : `${t("revenue.usd")} / ${t("revenue.oi")}`
            }
          >
            <Box display="flex" paddingRight="64px">
              {!!handleSwitchChange && (
                <BaseSwitch
                  label="TBG Revenue"
                  checked={showSecondBar?.[item.index]}
                  options={{ left: "Off", right: "On" }}
                  onChange={() => handleSwitchChange(item.index)}
                  fontWeight={400}
                  detailsView={true}
                />
              )}
            </Box>
          </ExpandedLayoutTitleWithActions>
          <ChartRootLayoutExpanded>
            <ParentSize debounceTime={10}>
              {({ width: visWidth, height: visHeight }) => (
                <BarChart
                  tableData={data}
                  width={!!visWidth ? visWidth - 64 : 0}
                  height={visHeight}
                  search={search}
                  entity={item.key}
                  firstBarInfos={firstBarInfos}
                  secondBarInfos={secondBarInfos}
                  dataType={dataType}
                  availableEntities={availableEntities}
                  entityIndex={item.index}
                  showSecondBar={showSecondBar?.[item.index]}
                  isExpanded
                />
              )}
            </ParentSize>
          </ChartRootLayoutExpanded>
        </ChartColumn>

        <Box display="flex" flexDirection="column">
          <Box display="flex" justifyContent="flex-end" marginBottom="42px">
            <CloseDialogButton
              onClick={() => {
                setOpen(false)
              }}
            />
          </Box>
          <FiltersCharts
            itemKey={item.key}
            legendItems={legendItems[item.index]}
            itemIndex={item.index}
            availableEntities={availableEntities}
            handleChangeAvailableEntities={handleChangeAvailableEntities}
            isOpen={true}
            handleOpen={() => null}
            isExpanded
            handleCheckAllEntities={handleCheckAllEntities}
            checked={selectAllChecked[item.index]}
          />
        </Box>
      </Box>
    </BaseChartDialog>
  )
})
